import { useEffect, useMemo, useState } from "react";
import { useFetcher } from "react-router";

import type { ArticleTileDTO, loader } from "~/routes/resources/article-grid";

/**
 * SFCC-11498 (ARTICLES-12) — port of composable's `useArticlePagination` +
 * `useBuilderPagination`. Composable fetched in the browser via React Query and
 * paged server-side (infinite query). Here the `/resources/article-grid`
 * resource route returns the full filtered tile list (the corpus is small —
 * ~341 articles/site, smaller per topic), so pagination is a pure client-side
 * slice: show 10, Show more (+10), Show all.
 */

const RESOURCE_URL = "/resources/article-grid";
export const DEFAULT_ITEMS_PER_LOAD = 10;

export interface PaginatedSlice<T> {
  displayedItems: T[];
  displayedCount: number;
  totalCount: number;
  /** Reveal the next `perLoad` items. */
  showNextEntries: () => void;
  /** Reveal everything. */
  showAll: () => void;
  allEntriesDisplayed: boolean;
}

/**
 * Client-side slice over an in-memory array. Resets to `perLoad` whenever the
 * source array identity changes (e.g. a topic switch refetches a new list).
 */
export function usePaginatedSlice<T>(
  items: T[],
  perLoad: number = DEFAULT_ITEMS_PER_LOAD,
): PaginatedSlice<T> {
  const step = perLoad > 0 ? perLoad : DEFAULT_ITEMS_PER_LOAD;
  const [displayedCount, setDisplayedCount] = useState(step);

  useEffect(() => {
    setDisplayedCount(step);
  }, [items, step]);

  const total = items.length;
  const cappedCount = Math.min(displayedCount, total);
  const displayedItems = useMemo(
    () => items.slice(0, cappedCount),
    [items, cappedCount],
  );

  return {
    displayedItems,
    displayedCount: cappedCount,
    totalCount: total,
    showNextEntries: () =>
      setDisplayedCount(c => Math.min(c + step, total || step)),
    showAll: () => setDisplayedCount(total),
    allEntriesDisplayed: cappedCount >= total,
  };
}

export interface UseArticlePaginationResult
  extends PaginatedSlice<ArticleTileDTO> {
  isLoading: boolean;
  error: string | null;
}

/**
 * Dynamic grid: load tiles from the resource route (optionally topic-filtered),
 * then slice client-side. A topic change creates a fresh fetcher (keyed on the
 * slug), so `isLoading` correctly re-asserts while the new list loads.
 *
 * SFCC-11724: `seedItems` is the articles-hub SSR-fetched DEFAULT (no-topic)
 * list. When present and no topic is selected, the default grid renders those
 * tiles on the first paint (no client round-trip, no cold skeleton) and the
 * fetcher load is skipped — the route loader already fetched the same SWR-cached
 * list server-side. A topic-filtered view ignores the seed and fetches as before.
 *
 * An EMPTY seed is treated as no seed at all. `fetchArticleGridList` fails soft
 * to `[]`, so a seed of `[]` is indistinguishable from "the SSR fetch did not
 * make it" — and the previous `seedItems != null` test accepted it, skipped the
 * client load, and left the grid on "Showing 0 of 0 articles" with no way back.
 *
 * That is not hypothetical. Measured on hsa-production 2026-08-16: a cold
 * `list:all` cache made the loader's seed fetch take 8.9s and fail soft, the
 * page rendered empty, and the browser never retried — while
 * `/resources/article-grid` answered the very same list in 0.68s. The cache key
 * carries the build version, so EVERY DEPLOY cold-starts this and the first
 * visitor per colo saw a blank hub until they reloaded by hand.
 *
 * Treating `[]` as absent costs nothing when the seed is genuinely populated and
 * makes the empty case self-healing. A truly empty article list (none published)
 * simply fetches once and confirms it.
 */
export function useArticlePagination(
  topicSlug: string | undefined,
  perLoad: number = DEFAULT_ITEMS_PER_LOAD,
  seedItems?: ArticleTileDTO[],
): UseArticlePaginationResult {
  const fetcher = useFetcher<typeof loader>({
    key: `article-grid-${topicSlug ?? "all"}`,
  });

  // Seed applies only to the default (no-topic) view; a topic must fetch.
  // `.length > 0` is load-bearing — see the note above.
  const seeded = !topicSlug && seedItems != null && seedItems.length > 0;

  useEffect(() => {
    // Skip the client load when the default view is SSR-seeded — the route
    // loader already fetched the (cached) default list. A topic switch always
    // fetches (its slug isn't covered by the no-topic seed). An empty seed is a
    // failed seed, so it must NOT suppress the load.
    if (!topicSlug && seedItems != null && seedItems.length > 0) return;
    const qs = topicSlug ? `?topic=${encodeURIComponent(topicSlug)}` : "";
    fetcher.load(`${RESOURCE_URL}${qs}`);
    // fetcher identity is stable per key; reload only when the topic changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicSlug]);

  const items = useMemo(
    // Prefer a landed fetcher response; otherwise fall back to the SSR seed for
    // the default view (the fetcher never runs in that case).
    () => fetcher.data?.items ?? (seeded ? seedItems : []),
    [fetcher.data, seeded, seedItems],
  );
  const slice = usePaginatedSlice(items, perLoad);

  return {
    ...slice,
    // First-load only: true until the first response lands. A topic switch
    // changes the fetcher key (new fetcher, no data) so this re-asserts. This
    // route has no mutations targeting it, so a same-key revalidation (which
    // would keep `data` populated) isn't a concern. A seeded default view is
    // never loading — the tiles are already present from SSR.
    isLoading: !seeded && fetcher.state !== "idle" && !fetcher.data,
    error: fetcher.data?.error ?? null,
  };
}

export interface UseArticleRefTileResult {
  tile: ArticleTileDTO | null;
  isLoading: boolean;
}

/**
 * Resolve a single article/topic reference to a tile via the resource route.
 * Used as a fallback by ArticleCard / ArticleTopicCard when the Builder
 * reference `value` isn't server-resolved (e.g. the editor canvas). No-ops when
 * `refId` is undefined (the server-resolved fast path).
 */
export function useArticleRefTile(
  refId: string | undefined,
  kind: "article" | "topic",
): UseArticleRefTileResult {
  const fetcher = useFetcher<typeof loader>({
    key: `article-ref-${kind}-${refId ?? "none"}`,
  });

  useEffect(() => {
    if (!refId) return;
    const param = kind === "article" ? "articleRefId" : "topicRefId";
    fetcher.load(`${RESOURCE_URL}?${param}=${encodeURIComponent(refId)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId, kind]);

  // Guard on `refId` so a no-op (server-resolved fast path) never hands back a
  // tile left over from a previous fetch — the hook's contract is "no tile
  // unless this ref was fetched".
  return {
    tile: refId ? (fetcher.data?.items?.[0] ?? null) : null,
    isLoading: !!refId && fetcher.state !== "idle" && !fetcher.data,
  };
}
